<template>
    <reo-dialog
        ref="help-me-choose-dialog"
        class="help-me-choose-dialog"
        :title="$t('help_me_choose_dialog_title')"
        @cancel="handleCancel"
    >
        <div class="data-desc">
            <ul class="data-desc-list">
                <li><ReoLink :href="config.helpMeChooseSupport" :text="$t('help_me_choose_dialog_support_text')"/></li>
                <li>{{ $t('help_me_choose_dialog_file_size') }}</li>
            </ul>
        </div>

        <div class="compare-plan">
            <el-table
                :data="data"
                :header-cell-style='getTitleClass'
                :cell-style='getRowClass'
                ref='planDesTable'>
                <el-table-column
                    v-for="col in cols"
                    :prop="col.prop"
                    :key='col.prop'
                    :align="col.align"
                    :width='TableColSize'
                    :fixed="col.fixed?col.fixed:false"
                >
                    <template v-slot:header>
                        <div class='header'>
                            <p v-html='col.prop'></p>
                        </div>
                    </template>
                    <template slot-scope="scope">
                        <template v-if="typeof scope.row[scope.column.property]==='boolean'">
                            <icon-right v-if="scope.row[scope.column.property]"></icon-right>
                            <span v-else>-</span>
                        </template>
                        <template v-else>
                            <span>{{ scope.row[scope.column.property] }}</span>
                        </template>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <section id="faqs-wap" class="faqs-wrap">
            <div id="faqs" class="faqs">
                <p class="title">{{ $t( 'homepage.faqsTitle' ) }}</p>
                <reo-collapse
                    :data="faqs"
                ></reo-collapse>
            </div>
        </section>
    </reo-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import ReoCollapse from '@/components/Collapse';
import IconRight from '@/components/Svg/IconRight.vue';
import { config } from 'CONFIG';
import { IPlanByStrategyAndCycle, IStrategyMap, productsManager } from '@/lib';
import { ESeriesCategory } from '@reolink-cloud/interface';
import { calcFinalDataStr } from '@/common/js/utils';
import ReoLink from '@/basic/Link.vue';
import { getPlanDataSizeStr } from '@/utils/business';

export default Vue.extend({
    name: 'HelpPlanChooseDialog',
    components: {
        ReoDialog,
        ReoCollapse,
        IconRight,
        ReoLink
    },
    props: {
        country: String,
        isSim: {
            type:Boolean,
            default:true
        },
        type: {
            type: [String, Array] as PropType<ESeriesCategory | ESeriesCategory[]>,
            default: ESeriesCategory.CLOUD_STORAGE_DATA_PLAN
        },
    },
    data () {

        return {
            config,
            cols: [
                {
                    prop: 'features',
                    align: 'center',
                    fixed: true
                }
            ],
            data: this.isSim ? [
                {
                    features: this.$t( 'help_me_choose_dialog_column_sim_data' )
                },
                // {
                //     features: this.$t( 'help_me_choose_dialog_column_cloud_history' )
                // },
                // {
                //     features: this.$t( 'help_me_choose_dialog_column_ai_recognition' )
                // },
                // {
                //     features: this.$t( 'help_me_choose_dialog_column_rich_notifications' )
                // }

            ] : [
                {
                    features: this.$t( 'help_me_choose_dialog_column_sim_data' )
                },
                {
                    features: this.$t( 'help_me_choose_dialog_column_cloud_history' )
                },
            ],
            activeName: '1',
            faqs: [
                {
                    title: this.$t( 'help_me_choose_dialog_faq_question_1' ) as string,
                    content: this.$t( 'help_me_choose_dialog_faq_answer_1' ) as string,
                },
                {
                    title: this.$t( 'help_me_choose_dialog_faq_question_2' ) as string,
                    content: this.$t( 'help_me_choose_dialog_faq_answer_2' ) as string,
                }
            ]
        };

    },
    async created() {

        await this.initPlanSeries();

    },
    methods: {
        async initPlanSeries() {

            const types = Array.isArray( this.type ) ? this.type : [this.type];

            const data: IPlanByStrategyAndCycle = types.reduce<IPlanByStrategyAndCycle>(( acc, cat ) => {

                const single = productsManager.getPlanSeriesMap({
                    category: cat,
                    country: this.country
                });
                return { ...acc, ...single };

            }, {});

            for ( let strategyMap of Object.values( data )) {

                for ( let cycleMap of Object.values( strategyMap as IStrategyMap )) {

                    for ( let plans of Object.values( cycleMap )) {

                        for ( let plan of plans ) {

                            const label = await this.getLabel( plan.id );
                            this.cols.push({
                                prop: label,
                                align: 'center',
                                fixed: false
                            });

                            this.data[0][label] = getPlanDataSizeStr( plan, this.$i18n );
                            if ( !this.isSim ) {

                                this.data[1][label] = plan.style['cloud_storage.default_retention_day'] ?? plan.style['cloud_storage.video_lifetime'];

                            }

                            // this.data[2][label] = plan.style['cloud_storage.allow_ai'] ? true : false;
                            // this.data[3][label] = plan.style['cloud_storage.allow_rich_notification'] ? true : false;

                        }

                    }

                }

            }

        },
        handleCancel() {

            this.$emit( 'cancel' );

        },
        getTitleClass () {

            let style = 'font-weight:600;background-color:var(--bg_03_h);color:#333333;border-bottom:0;';

            if ( this.isMobile ) {

                return style + 'font-size:14px;line-height:1.2;padding: 10px 0;';

            } else {

                return style + 'font-size:14px;line-height:1.2;padding: 14px 0;';

            }

        },
        getRowClass ({ column }) {

            let style = 'background-color:#FFF;color:#333333';
            if ( column.property === 'traffic' ) {

                style += 'font-weight:500;';

            } else {

                style += 'font-weight:400;';

            }

            if ( this.isMobile ) {

                return style + 'font-size:14px;line-height:1.2;padding: 10px;';

            } else {

                return style + 'font-size:14px;line-height:1.2;padding: 14px;';

            }

        },
        getColumnWidth ( pc: number ): number {

            const html = document.getElementsByTagName( 'html' )[0];
            if ( this.isMobile ) {

                return 120;

            } else {

                return parseFloat( window.getComputedStyle( html ).fontSize ) * pc;

            }

        },
        async getLabel ( /** planId */ type: string ): Promise<string> {

            const planInfo = await productsManager.getPlanById( type );
            const seriesInfo = productsManager.getPlanSeriesByKey( planInfo.seriesKey );
            return `<p p_id=${type}>${seriesInfo.i18n.name}<br/></p>`;

        }
    },
    computed: {

        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },
        TableColSize (): number {

            return this.getColumnWidth( 1.8 );

        }

    },
    watch: {
        async country() {

            await this.initPlanSeries();

        }
    }

});
</script>

<style lang="less" scoped>
    .help-me-choose-dialog{
        color: @font_primary;
        font-weight: 500;
        /deep/ .reo-dialog-inner{
            background-color: @bg_01 !important;
            @media @snapeak_mobile{
                width: 100%;
            }
        }
        .data-desc{
            text-align: left;
            font-size: 14px;
            line-height: 18px;
            ul.data-desc-list{
                display: flex;
                column-gap: 24px;
                row-gap: 12px;
                @media @snapeak_mobile{
                    row-gap: 8px;
                }
                flex-wrap: wrap;
                li{
                    display: inline-flex;
                    gap: 8px;
                    &::before{
                        content: '';
                        width: 8px;
                        height: 8px;
                        background-color: @brand_light;
                        border-radius: 50%;
                        align-self: center;
                    }
                    &:last-child{
                        width: 100%;
                    }
                    @media @snapeak_mobile{
                        width: 100%;
                    }
                    /deep/ .ui-link .text{
                        font-size: 14px;
                    }
                    /deep/ .ui-link .ui-icon{
                        font-size: 12px;
                    }
                }
            }
        }
        .compare-plan{
            margin-top: 30px;
        }
        .faqs-wrap{
            text-align: left;
            margin-top: 50px;
            margin-bottom: 40px;
            .title{
                margin: 20px 0;
                font-size: 20px;
                line-height: 1.2;
                font-weight: 500;
            }
        }
    }
</style>

<style lang="less" scoped>

    // 当表格没有滚动条时
    /deep/ .el-table__body-wrapper.is-scrolling-none~.el-table__fixed-right{
        height: 100% !important;
        bottom: 0 !important;
        box-shadow: none !important;
    }
    // 当表格有纵向滚动条时
    /deep/ .el-table--scrollable-y .el-table__fixed-right{
        right: 7px !important;
    }
    // 当表格只有横向滚动条，没有纵向滚动条时
    /deep/ .el-table--scrollable-x:not(.el-table--scrollable-y) .el-table__fixed-right{
        right: 0 !important;
    }
    @media @snapeak_mobile{
        /deep/ .el-table__fixed,
        /deep/ .el-table__fixed-right{
        height: calc(100% - 8px) !important;
        }
        // 解决表格固定列问题
        /deep/ .el-table__fixed,
        /deep/ .el-table__fixed-right{
            // height: calc(100% - 6px) !important;
            box-shadow: -5px -2px 10px rgba(0,0,0,.5) !important;
        }
    }
    /deep/ .el-table__expand-column .cell {
        .el-table__expand-icon--expanded{
            transform: rotate(180deg);
        }
        .el-table__expand-icon{
            .el-icon-arrow-right::before{
                content:"\E6E1"
            }
        }
    }
    .el-table /deep/ .cell{
        word-break: keep-all;
        word-break: auto-phrase;
        padding:0;
    }
    .el-table /deep/ .header{
        line-height: 1.2;
    }
    .el-table{
        background-color: @bg_02;
        &::before{
            height: 0;
        }
        & /deep/ .el-table__fixed:before{
            height: 0;
        }
        & /deep/ .el-table__body-wrapper{
            &::-webkit-scrollbar{
                width: 4px;
                height: 4px;
            }
            &::-webkit-scrollbar-thumb{
                background-color: #CCCCCC;
                border-radius: 8px;
            }
            // 隐藏所有箭头按钮
            &::-webkit-scrollbar-button{
                display: none;
            }
        }
        & /deep/ tr{
            background-color: transparent;
        }
    }

    .el-collapse {
        border-bottom: 1px solid @line_light;
        border-top: 1px solid @line_light;
        /deep/ .el-collapse-item__header{
            background-color: @bg_03;
            color: @font_primary;
            border-bottom: 1px solid @line_light;
        }
        /deep/ .el-collapse-item__wrap{
            background-color: @bg_03;
            color: @font_primary;
            border-bottom: 1px solid @line_light;
            .el-collapse-item__content{
                color: @font_primary;
            }

        }
    }
.reo-dialog.help-me-choose-dialog{
    /deep/ .reo-dialog-inner{
        @media @pc{

            max-width: 1200px;
        }
        .dialog-title{
            text-align: center;
        }
    }
}

/deep/ .el-table__header-wrapper{
    background-color: @bg_03_h;
}
/deep/ .el-table__fixed{
    transform: translate3d(0,0,0); //解决safari的渲染问题
}

//在safari下弹窗的内容会将滚动条盖住，故需要将滚动条右移避免此问题
.reo-dialog.help-me-choose-dialog /deep/ .reo-dialog-inner .scroll-context{
    @media @mobile{
        position: relative;
        width: calc(100% + 32px);
        box-sizing: border-box;
        padding: 0 16px;
        left: -16px;
    }
}

/deep/ p, span, li{
    font-weight: 500;
}
</style>
